import type { ContinueWatchingEntry } from "@core/home/types";
import { formatDuration, URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { CirclePlay, Play } from "lucide-react";

export function ContinueWatchingSection({
  entries = [],
}: {
  entries?: ContinueWatchingEntry[];
}) {
  if (entries.length === 0) return null;

  return (
    <section className="rounded-[2rem] border border-cyan-100 bg-gradient-to-br from-cyan-50 to-white p-5 shadow-sm dark:border-cyan-950 dark:from-cyan-950/30 dark:to-slate-950 sm:p-7">
      <div className="mb-5 flex items-start gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-600 text-white shadow-lg shadow-cyan-600/20">
          <CirclePlay className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-xl font-black text-slate-950 dark:text-white">
            {trans("home.continueWatching")}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {trans("home.continueWatchingDescription")}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {entries.slice(0, 6).map((entry) => (
          <Link
            key={`${entry.lesson.id}-${entry.resource.id}`}
            to={URLS.lessons.view(entry.lesson)}
            className="group rounded-2xl border border-cyan-100 bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-100 text-cyan-700 transition group-hover:bg-cyan-600 group-hover:text-white dark:bg-cyan-950 dark:text-cyan-300">
                <Play className="h-4 w-4 fill-current" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-sm font-black text-slate-900 dark:text-white">
                  {entry.lesson.name}
                </h3>
                <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                  {entry.resource.title || entry.lesson.name}
                </p>
              </div>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-500"
                style={{
                  width: `${Math.max(2, Math.min(100, entry.progressPercentage))}%`,
                }}
              />
            </div>
            <div className="mt-2 flex justify-between text-xs font-bold text-slate-500 dark:text-slate-400">
              <span>
                {trans("home.watchedPercentage", {
                  percentage: Math.round(entry.progressPercentage),
                })}
              </span>
              <span>{formatDuration(entry.resumeAtSeconds)}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
